// TMOS Setup Wizard - Position Role Mapping Step
// Date: December 19, 2025
// Purpose: Position to role mapping step for tenant admin setup

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { businessPositionService } from '@/services/businessPositionService';
import { BusinessPosition } from '@/services/businessPositionService';

interface PositionRoleMappingStepProps {
  setupData: Record<string, any>;
  onComplete: (data: Record<string, any>) => void;
  onNext: () => void;
  loading: boolean;
}

const SYSTEM_ROLES = [
  { value: 'T_ADMIN', label: 'Tenant Administrator' },
  { value: 'T_USER', label: 'Tenant User' },
  { value: 'C_ADMIN', label: 'Company Administrator' },
  { value: 'C_USER', label: 'Company User' },
  { value: 'P_ADMIN', label: 'Project Administrator' },
  { value: 'P_USER', label: 'Project User' }
];

export const PositionRoleMappingStep: React.FC<PositionRoleMappingStepProps> = ({
  setupData,
  onComplete,
  loading
}) => {
  const { t } = useLanguage();
  const [positions, setPositions] = useState<BusinessPosition[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>(
    setupData.positionRoleMappings || {}
  );

  useEffect(() => {
    loadPositions();
  }, []);

  const loadPositions = async () => {
    try {
      const activePositions = await businessPositionService.getActivePositions();
      setPositions(activePositions);
      
      // Load existing mappings
      const existingMappings: Record<string, string> = {};
      for (const position of activePositions) {
        const primaryRole = await businessPositionService.getPrimaryRoleForPosition(position.id);
        if (primaryRole) {
          existingMappings[position.id] = primaryRole;
        }
      }
      setMappings({ ...mappings, ...existingMappings });
    } catch (error) {
      console.error('Error loading positions:', error);
    }
  };

  const handleMappingChange = (positionId: string, roleCode: string) => {
    setMappings({ ...mappings, [positionId]: roleCode });
  };

  const handleContinue = () => {
    onComplete({
      position_role_mapping: {
        mappings
      },
      positionRoleMappings: mappings
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 mb-6">
        {t('setupWizard.positionRole.description') || 
          'Map business positions to system roles. Users with these positions will automatically receive the assigned roles.'}
      </p>

      <div className="space-y-4">
        {positions.map((position) => (
          <div
            key={position.id}
            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
          >
            <div className="flex-1">
              <div className="font-medium text-gray-900">{position.position_name}</div>
              {position.position_category && (
                <div className="text-sm text-gray-500">{position.position_category}</div>
              )}
            </div>
            <div className="w-64">
              <Select
                value={mappings[position.id] || ''}
                onChange={(e) => handleMappingChange(position.id, e.target.value)}
                options={[
                  { value: '', label: t('common.select') || 'Select role...' },
                  ...SYSTEM_ROLES
                ]}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={loading}>
          {t('common.continue') || 'Continue'}
        </Button>
      </div>
    </div>
  );
};

